import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
  lowProfileManualProductSourceSchema,
  ShippingOptionRenderTarget,
  ThresholdManualProductSourceSchema,
  UpsellProductDiscountSchema,
} from "./widget";

/**
 * Base schema for all checkout widgets in the collection
 */
const checkoutWidgetBaseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  type: createEnum(CheckoutOptimizationWidgetType),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const baseStandardWidgetSchema = checkoutWidgetBaseSchema.extend({
  // Authoritative flag for whether the widget applies to all checkouts by
  // default. `.default(false)` so lean reads of un-backfilled docs (which lack
  // the column and don't get Mongoose defaults) still parse.
  defaultEnabled: z.boolean().default(false),
  // Free-form render target. Known/default values come from
  // `PurchaseCheckoutBlockRenderTarget`, but merchants can place widgets at
  // arbitrary string targets (e.g. "block1").
  target: z.string(),
});

/**
 * Fulfillment Cutoff Widget schema
 */
const fulfillmentCutoffWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});

export interface FulfillmentCutoffWidget
  extends z.infer<typeof fulfillmentCutoffWidgetSchema> {}

/**
 * Threshold Upsell Widget schema
 */
const thresholdUpsellWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  threshold: z.object({ amount: z.number(), currency: createEnum(Currency) }),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  progressBarColor: z
    .object({ filled: z.string().optional(), unfilled: z.string().optional() })
    .optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(ThresholdManualProductSourceSchema).optional(),
  defaultProductDiscount: UpsellProductDiscountSchema.optional(),
});

export interface ThresholdUpsellWidget
  extends z.infer<typeof thresholdUpsellWidgetSchema> {}

/**
 * Low-profile Product Upsell Widget schema
 */
const lowProfileProductUpsellWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(lowProfileManualProductSourceSchema).optional(),
});

export interface LowProfileProductUpsellWidget
  extends z.infer<typeof lowProfileProductUpsellWidgetSchema> {}

/**
 * Content Block Widget schema
 */
const contentBlockWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
  sections: z
    .array(
      z.object({
        _id: z.string(),
        title: z.string().nullish(),
        description: z.string().nullish(),
        imageUrl: z.string().nullish(),
      }),
    )
    .max(5),
});

export interface ContentBlockWidget
  extends z.infer<typeof contentBlockWidgetSchema> {}

/**
 * Image Block Widget schema
 */
const imageBlockWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
  imageUrl: z.string().nullish(),
  imageWidthPx: z.coerce.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});

export interface ImageBlockWidget
  extends z.infer<typeof imageBlockWidgetSchema> {}

/**
 * Shipping Option Content Widget schema
 * Renders within shipping option items in checkout
 */
const shippingOptionContentWidgetSchema_ = checkoutWidgetBaseSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
  target: createEnum(ShippingOptionRenderTarget),
  title: z.string(),
  description: z.string(),
  imageUrl: z.string().nullish(),
  backgroundColor: z.string(),
});
export const shippingOptionContentWidgetSchema: z.ZodType<ShippingOptionContentWidget> =
  shippingOptionContentWidgetSchema_;
export interface ShippingOptionContentWidget
  extends z.infer<typeof shippingOptionContentWidgetSchema_> {}

/**
 * Shipping Option Image Widget schema
 * Renders an image within shipping option items in checkout
 */
const shippingOptionImageWidgetSchema_ = checkoutWidgetBaseSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE),
  target: createEnum(ShippingOptionRenderTarget),
  imageUrl: z.string().nullish(),
  imageWidth: z.string(),
  imageJustification: createEnum(CheckoutImageBlockJustification),
});
export const shippingOptionImageWidgetSchema: z.ZodType<ShippingOptionImageWidget> =
  shippingOptionImageWidgetSchema_;
export interface ShippingOptionImageWidget
  extends z.infer<typeof shippingOptionImageWidgetSchema_> {}

/**
 * Shipping Option Product Upsell Widget schema
 *
 * Upsells a product when the buyer selects a specific shipping method in
 * Shopify checkout, dropping down below the selected method. Combines the
 * low-profile product upsell data model (recommended products, manual
 * sources, excluded/fallback tags, optional per-slot discounts) with an
 * optional threshold progress bar.
 *
 * The progress bar is modeled as a single `nullish` sub-object so "off" is
 * simply its absence. Custom color fields are intentionally omitted — the
 * storefront renders this with Polaris web components (`s-progress`,
 * `s-button`) that ignore custom hex and degrade to Polaris defaults.
 */
const shippingOptionProductUpsellWidgetSchema_ =
  checkoutWidgetBaseSchema.extend({
    type: z.literal(
      CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL,
    ),
    target: createEnum(ShippingOptionRenderTarget),
    addToCartButtonText: z.string(),
    excludedProductTags: z.array(z.string()).optional(),
    fallbackProductTags: z.array(z.string()).optional(),
    manualProductSources: z
      .array(lowProfileManualProductSourceSchema)
      .optional(),
    defaultProductDiscount: UpsellProductDiscountSchema.optional(),
    progressBar: z
      .object({
        threshold: z.object({
          amount: z.number(),
          currency: createEnum(Currency),
        }),
        gteThresholdText: z.string(),
        ltThresholdText: z.string(),
      })
      .nullish(),
  });
export const shippingOptionProductUpsellWidgetSchema: z.ZodType<ShippingOptionProductUpsellWidget> =
  shippingOptionProductUpsellWidgetSchema_;
export interface ShippingOptionProductUpsellWidget
  extends z.infer<typeof shippingOptionProductUpsellWidgetSchema_> {}

/**
 * Failed Discount Rescue Widget schema
 *
 * Renders nothing visible in checkout. Subscribes (via the long-poll RPC) to
 * failed-discount-attempt events for the current checkout token. When one
 * arrives, shows a "we've got you covered" rescue UI with a Claim button
 * that applies the merchant-configured discount via
 * `useApplyDiscountCodeChange`.
 *
 * `discountId` references a `Discount` document — the same first-class
 * Discount entity that Spotlights, RECOVER, marketing agents, and other
 * Redo features reference. The actual code is generated on demand at claim
 * time via `getCodeForDiscount` from redo/discounts/service. Optional
 * because newly-created widgets may not have a discount selected yet; the
 * runtime claim handler treats a missing discountId as `widget-misconfigured`.
 */
const failedDiscountRescueWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE),
  discountId: zExt.objectId().optional(),
  text: z.string(),
  buttonText: z.string(),
});

export interface FailedDiscountRescueWidget
  extends z.infer<typeof failedDiscountRescueWidgetSchema> {}

/**
 * Product Reviews Widget schema
 *
 * Renders a carousel of reviews for products in the buyer's cart, sourced
 * from the merchant's configured reviews provider (e.g. Judge.me). Reviews
 * are fetched at render time — no review content is persisted on the widget.
 * If the provider integration is missing, disabled, or returns no qualifying
 * reviews, the widget renders nothing.
 */
const productReviewsWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.PRODUCT_REVIEWS),
  provider: createEnum(CheckoutReviewsProvider),
  minRating: z.number().int().min(1).max(5),
  display: z.object({
    showProductName: z.boolean(),
    showReviewTitle: z.boolean(),
    showReviewBody: z.boolean(),
    showReviewerName: z.boolean(),
    showVerifiedBadge: z.boolean(),
  }),
});

export interface ProductReviewsWidget
  extends z.infer<typeof productReviewsWidgetSchema> {}

/**
 * Union of all shipping option widget types
 */
const shippingOptionWidgetSchema_ = z.discriminatedUnion("type", [
  shippingOptionContentWidgetSchema_,
  shippingOptionImageWidgetSchema_,
  shippingOptionProductUpsellWidgetSchema_,
]);
export const shippingOptionWidgetSchema: z.ZodType<ShippingOptionWidget> =
  shippingOptionWidgetSchema_;
export type ShippingOptionWidget = z.infer<typeof shippingOptionWidgetSchema_>;

/**
 * Discriminated union of all checkout widget types
 */
const checkoutWidgetSchema_ = z.discriminatedUnion("type", [
  fulfillmentCutoffWidgetSchema,
  thresholdUpsellWidgetSchema,
  lowProfileProductUpsellWidgetSchema,
  contentBlockWidgetSchema,
  imageBlockWidgetSchema,
  shippingOptionContentWidgetSchema_,
  shippingOptionImageWidgetSchema_,
  shippingOptionProductUpsellWidgetSchema_,
  failedDiscountRescueWidgetSchema,
  productReviewsWidgetSchema,
]);
export const checkoutWidgetSchema: z.ZodType<CheckoutWidget> =
  checkoutWidgetSchema_;
export type CheckoutWidget = z.infer<typeof checkoutWidgetSchema_>;

const checkoutWidgetWithAssignmentsSchema_ = z.intersection(
  checkoutWidgetSchema,
  z.object({
    assignedExperienceIds: z.array(zExt.objectId()),
    assignedExperienceNames: z.array(z.string()),
  }),
);
export const checkoutWidgetWithAssignmentsSchema: z.ZodType<CheckoutWidgetWithAssignments> =
  checkoutWidgetWithAssignmentsSchema_;
export type CheckoutWidgetWithAssignments = z.infer<
  typeof checkoutWidgetWithAssignmentsSchema_
>;
